import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import {
  decideTitleTranslation,
  listTitleTranslations,
  suggestTitleTranslations,
  type TitleRow,
} from "@/lib/title-translations.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/_authenticated/admin/traducciones-titulos")({
  head: () => ({
    meta: [
      { title: "Traducción de títulos · Administración" },
      { name: "description", content: "Revisa y aprueba traducciones al español sugeridas por IA para los títulos de tours." },
      { property: "og:title", content: "Traducción de títulos · Administración" },
      { property: "og:description", content: "Sugerencias de IA para títulos de tours, con aprobación manual." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: Page,
});

const STATUS: Record<TitleRow["status"], string> = {
  new: "Sin sugerencia",
  pending: "Por revisar",
  approved: "Aprobado",
  rejected: "Rechazado",
};

function Page() {
  const list = useServerFn(listTitleTranslations);
  const suggest = useServerFn(suggestTitleTranslations);
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["title-translations"], queryFn: () => list() });
  const rows = data?.rows ?? [];
  const missing = rows.filter((r) => r.status === "new");

  const suggestMut = useMutation({
    mutationFn: () =>
      suggest({ data: { items: missing.slice(0, 20).map((r) => ({ bokunId: r.bokunId, title: r.originalTitle })) } }),
    onSuccess: (r) => {
      toast.success(`${r.count} sugerencias listas para revisar`);
      qc.invalidateQueries({ queryKey: ["title-translations"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6 p-4 md:p-8">
      <div>
        <h1 className="text-2xl font-bold">Traducción de títulos</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Tours cuyo título en español parece estar en inglés. La IA sugiere una traducción; solo se muestra en el sitio después de que la apruebes.
        </p>
      </div>
      <Button onClick={() => suggestMut.mutate()} disabled={!missing.length || suggestMut.isPending}>
        {suggestMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
        Sugerir traducciones ({Math.min(missing.length, 20)} de {missing.length})
      </Button>
      {isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      {!isLoading && !rows.length && <p className="text-sm text-muted-foreground">No hay títulos pendientes.</p>}
      <div className="grid gap-3">
        {rows.map((r) => (
          <RowCard key={r.bokunId} row={r} />
        ))}
      </div>
    </div>
  );
}

function RowCard({ row }: { row: TitleRow }) {
  const decide = useServerFn(decideTitleTranslation);
  const qc = useQueryClient();
  const [text, setText] = useState(row.approvedTitle ?? row.suggestedTitle ?? "");
  useEffect(() => setText(row.approvedTitle ?? row.suggestedTitle ?? ""), [row.approvedTitle, row.suggestedTitle]);
  const mut = useMutation({
    mutationFn: (decision: "approve" | "reject") =>
      decide({ data: { bokunId: row.bokunId, originalTitle: row.originalTitle, decision, title: text || undefined } }),
    onSuccess: (_r, d) => {
      toast.success(d === "approve" ? "Título aprobado" : "Sugerencia rechazada");
      qc.invalidateQueries({ queryKey: ["title-translations"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {row.originalTitle}{" "}
          <span className="ml-2 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">{STATUS[row.status]}</span>
        </CardTitle>
        <p className="text-xs text-muted-foreground">Bókun {row.bokunId}</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 sm:flex-row">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Título en español" maxLength={140} />
        <div className="flex gap-2">
          <Button size="sm" onClick={() => mut.mutate("approve")} disabled={mut.isPending || text.trim().length < 3}>
            Aprobar
          </Button>
          <Button size="sm" variant="outline" onClick={() => mut.mutate("reject")} disabled={mut.isPending}>
            Rechazar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
